import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  doesValueExist,
  getAskingPrice,
  sentryError,
  isImage,
  getIsListingOwner,
} from 'utils';
import {
  INCLUDE_VIDEO_IN_TEASER,
  Listing,
  TEASER_TYPE,
  TEASER_VERIFICATION,
} from 'models/Listing';
import TagIcon from 'icons/Tag.icon';
import SuitcaseIcon from 'icons/Suitcase.icon';
import {
  AskingPricePlaceholder,
  KeyDetailsPlaceholder,
  MainPlaceholder,
  MapPlaceholder,
  TitlePlaceholder,
  VideoPlaceholder,
  WhatsNextPlaceholder,
} from './placeholders';
import { checkIfNotNegative, getIntegerValue, getPricePerField } from './utils';
import LocationmarkerIcon from 'icons/Locationmarker.icon';
import ResolvePopup from 'components/ResolvePopup';
import { Map } from './components/Map';
import useMatchMedia from 'hooks/useMatchMedia';
import { BussinessVideo } from './components/BussinessVideo';
import { WhatsNextSection } from './components/WhatsNextSection';
import useGetKeyDetailsOptions from './hooks';
import useShowFieldVisibleInfo from 'hooks/useShowFieldVisibleInfo';
import { PdfViewer } from 'components/PdfViewer';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthContext from 'providers/Auth/Auth.context';
import { ASKING_PRICE_SELECT } from 'models/DealRoom';
import { MainLayoutContext } from 'router/subrouters/Dash/Dash.router';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import NudgeInterested from 'components/NudgeInterested';
import api from 'api';
import { BUSINESS_ROUTE } from 'data/global';
import EnquiryContext from 'providers/Enquiry/Enquiry.context';
import { getImgUrl, RESIZED_WIDTH } from 'components/ResolveImage/utils';

import './BusinessTeaser.styles.scss';
import './BusinessTeaser.styles.responsive.scss';

const DISABLE_FOOTER_CLASS = 'business-teaser-footer--disabled';

type BusinessTeaserProps = {
  className?: string;
  isLoading?: boolean;
  listing: Partial<Listing>;
  isPreview?: boolean;
};

const BusinessTeaser: FC<BusinessTeaserProps> = ({
  className,
  listing,
  isLoading,
  isPreview = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isLoggedIn, previousLocation } = useContext(AuthContext);
  const { currentUser, isSuperAdmin } = useContext(CurrentUserContext);
  const { addFooterClassName, resetFooterClassName } =
    useContext(MainLayoutContext);
  const { onEnquire } = useContext(EnquiryContext);

  const classes = classNames('business-teaser', className);

  const { hash: openEnquiry } = useLocation();

  const isMobile = useMatchMedia('isPhablet');

  const isListingOwner = getIsListingOwner(
    currentUser?._id,
    listing?.userId,
    listing?.isCoOwner,
  );

  const dontShowEnquire =
    isLoading || isSuperAdmin || isListingOwner || listing?.isEnquired;

  const needsToBeLoggedIn =
    listing?.teaserVerification ===
      TEASER_VERIFICATION.WITH_EMAIL_VERIFICATION && !isLoggedIn;
  const enableTeaser = listing ? listing.enableTeaser : true;

  const hasTeaserFile =
    listing?.teaserType === TEASER_TYPE.OWN_TEASER_FILE &&
    !!listing?.teaserFile;

  const isFixedPrice =
    listing?.askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE;
  const isEOI =
    listing?.askingPriceSelect === ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST;

  const adjEBITDA = useMemo(() => {
    const { EBITDA, totalAdjustments } = listing || {};
    const intEbitda = getIntegerValue(EBITDA);
    const intAdj = getIntegerValue(totalAdjustments);

    if (!intEbitda || intEbitda < 0) return;
    if (!totalAdjustments) return intEbitda;

    return intEbitda + intAdj;
  }, [listing]);

  const adjEBIT = useMemo(() => {
    const { EBIT, totalAdjustments } = listing || {};
    const intEbit = getIntegerValue(EBIT);
    const intAdj = getIntegerValue(totalAdjustments);

    if (!intEbit || intEbit < 0) return;
    if (!totalAdjustments) return intEbit;

    return intEbit + intAdj;
  }, [listing]);

  const keyDetailsOptions = useGetKeyDetailsOptions(
    listing,
    isFixedPrice,
    isEOI,
    adjEBITDA,
    isPreview,
  );

  const { t } = useTranslation();

  const navigate = useNavigate();

  const getShowFieldVisibleInfo = useShowFieldVisibleInfo();

  const askingPriceVisibleInfo = isFixedPrice
    ? listing?.amountRequiredVisibleInfo
    : listing?.amountFromRequiredVisibleInfo ||
      listing?.amountToRequiredVisibleInfo;

  const {
    isHidden: isAskingPriceHidden,
    reasonToHideContent: askingPriceReason,
  } = getShowFieldVisibleInfo(askingPriceVisibleInfo);

  const { isHidden: isRevenueHidden, reasonToHideContent: revenueReason } =
    getShowFieldVisibleInfo(listing?.revenueRequiredVisibleInfo);

  const { isHidden: isEBITDAHidden, reasonToHideContent: EBITDAReason } =
    getShowFieldVisibleInfo(listing?.EBITDARequiredVisibleInfo);

  const { isHidden: isEBITHidden, reasonToHideContent: EBITReason } =
    getShowFieldVisibleInfo(listing?.EBITRequiredVisibleInfo);

  const { isHidden: isCOGSHidden, reasonToHideContent: COGSReason } =
    getShowFieldVisibleInfo(listing?.COGSRequiredVisibleInfo);

  const {
    isHidden: isLessOverheadsHidden,
    reasonToHideContent: lessOverheadsReason,
  } = getShowFieldVisibleInfo(listing?.lessOverheadsRequiredVisibleInfo);

  const {
    isHidden: isDepreciationHidden,
    reasonToHideContent: depreciationReason,
  } = getShowFieldVisibleInfo(
    listing?.depreciationAndAmortisationRequiredVisibleInfo,
  );

  const {
    isHidden: isAccountingPeriodHidden,
    reasonToHideContent: accountingPeriodReason,
  } = getShowFieldVisibleInfo(listing?.accountingPeriodRequiredVisibleInfo);

  const keyDetails = useMemo(
    () =>
      isLoading ? (
        <KeyDetailsPlaceholder />
      ) : (
        <div
          className={classNames('white-panel', 'business-teaser__key-details')}
        >
          <p>{t('keyDetails')}</p>
          <div>
            {keyDetailsOptions.map(
              ({ icon, text, popupText, value, popupIcon }) => {
                if (!value) return null;

                return (
                  <span key={text}>
                    {icon}
                    <span className="price">{value}</span>
                    <span>{t(text)}</span>

                    {popupText && (
                      <ResolvePopup
                        onHover
                        contentClassName="business-teaser__popup"
                        popupContent={t(popupText)}
                      >
                        {popupIcon}
                      </ResolvePopup>
                    )}
                  </span>
                );
              },
            )}
          </div>
        </div>
      ),
    [isLoading, keyDetailsOptions, t],
  );

  const askingPrice = useMemo(() => {
    if (isEOI) return null;

    const listingPrice = isFixedPrice ? listing?.amount : listing?.amountFrom;
    const intListingPrice = getIntegerValue(listingPrice);

    const EBITDA = intListingPrice / adjEBITDA;
    const EBIT = intListingPrice / adjEBIT;

    const askingPrice = getAskingPrice({
      ...listing,
      amount: getIntegerValue(listing?.amount),
      amountFrom: getIntegerValue(listing?.amountFrom),
      amountTo: getIntegerValue(listing?.amountTo),
    });

    const askingPriceDisplay =
      isPreview && isAskingPriceHidden
        ? askingPriceReason
        : askingPrice || (isAskingPriceHidden ? askingPriceReason : null);

    const EBITDAMultipleDisplay = isAskingPriceHidden
      ? '-'
      : isEBITDAHidden
      ? EBITDAReason
      : checkIfNotNegative(EBITDA)
      ? `${EBITDA.toFixed(1)}x`
      : '-';

    const EBITMultipleDisplay = isAskingPriceHidden
      ? '-'
      : isEBITHidden
      ? EBITReason
      : checkIfNotNegative(EBIT)
      ? `${EBIT.toFixed(1)}x`
      : '-';

    return isLoading ? (
      <AskingPricePlaceholder />
    ) : (
      <div
        className={classNames('white-panel', 'business-teaser__asking-price')}
      >
        <div>
          <TagIcon /> {t('askingPrice')} ($)
          <span>{askingPriceDisplay}</span>
        </div>
        <div>
          <TagIcon />
          <p>
            {t('askingPrice')} / {t('multiple')}
          </p>
          <span>
            <p>{t('adjEBITDALC')}</p>
            {EBITDAMultipleDisplay}
          </span>
          <span>
            <p>{t('adjEBITLC')}</p>
            {EBITMultipleDisplay}
          </span>
        </div>
      </div>
    );
  }, [
    isEOI,
    adjEBIT,
    adjEBITDA,
    isFixedPrice,
    isLoading,
    listing,
    t,
    isPreview,
    isAskingPriceHidden,
    askingPriceReason,
    isEBITDAHidden,
    EBITDAReason,
    isEBITHidden,
    EBITReason,
  ]);

  const plStatement = useMemo(() => {
    const {
      revenue,
      COGS,
      grossProfit,
      EBITDA,
      adjustments,
      totalAdjustments,
      lessOverheads,
      depreciationAndAmortisation,
      accountingPeriod,
    } = listing || {};

    const filteredAdjustments = adjustments
      ?.filter(Boolean)
      ?.filter((el) => !!el?.name);

    const showAccountingPeriodRow =
      doesValueExist(accountingPeriod) || isAccountingPeriodHidden;
    const showRevenueRow = doesValueExist(revenue) || isRevenueHidden;
    const showCOGSRow = doesValueExist(COGS) || isCOGSHidden;
    const showLessOverheadsRow =
      doesValueExist(lessOverheads) || isLessOverheadsHidden;
    const showDepreciationRow =
      doesValueExist(depreciationAndAmortisation) || isDepreciationHidden;
    const showEBITDARow = checkIfNotNegative(EBITDA) || isEBITDAHidden;

    return isLoading ? (
      <MainPlaceholder />
    ) : (
      <div className={classNames('white-panel', 'business-teaser__statement')}>
        {showAccountingPeriodRow && (
          <div className="bold title">
            {t('plStatement')}{' '}
            <p>
              {doesValueExist(accountingPeriod) &&
              !(isPreview && isAccountingPeriodHidden)
                ? accountingPeriod
                : accountingPeriodReason}
            </p>
          </div>
        )}
        {showRevenueRow && (
          <div className="bold">
            <p>{t('revenue')}</p>{' '}
            <p>
              {doesValueExist(revenue) && !(isPreview && isRevenueHidden)
                ? getPricePerField(revenue)
                : revenueReason}
            </p>
          </div>
        )}
        {showCOGSRow && (
          <div>
            <p>{t('COGS')}</p>{' '}
            <p>
              {doesValueExist(COGS) && !(isPreview && isCOGSHidden)
                ? getPricePerField(COGS)
                : COGSReason}
            </p>
          </div>
        )}
        {doesValueExist(grossProfit) && !isRevenueHidden && !isCOGSHidden && (
          <div className="bold border">
            <p>{t('grossProfit')}</p>
            <p>{getPricePerField(grossProfit)}</p>
          </div>
        )}
        {showLessOverheadsRow && (
          <div className="border">
            <p>{t('operatingExpenses')}</p>
            <p>
              {doesValueExist(lessOverheads) &&
              !(isPreview && isLessOverheadsHidden)
                ? getPricePerField(lessOverheads)
                : lessOverheadsReason}
            </p>
          </div>
        )}
        {showDepreciationRow && (
          <div className="bold-border">
            <p>{t('depreciationAndAmortisation')}</p>
            <p>
              {doesValueExist(depreciationAndAmortisation) &&
              !(isPreview && isDepreciationHidden)
                ? getPricePerField(depreciationAndAmortisation)
                : depreciationReason}
            </p>
          </div>
        )}
        {showEBITDARow && (
          <div className="bold bold-border">
            <p>{t('EBITDA')}</p>
            <p>
              {checkIfNotNegative(EBITDA) && !(isPreview && isEBITDAHidden)
                ? getPricePerField(EBITDA)
                : EBITDAReason}
            </p>
          </div>
        )}
        {!!filteredAdjustments?.length && !isEBITDAHidden && (
          <div>{t('adjustments')}</div>
        )}
        {!!filteredAdjustments?.length && !isEBITDAHidden && (
          <ul>
            {filteredAdjustments.map((adjustment) => (
              <li key={adjustment?.name}>
                <p>{adjustment?.name}</p>
                <p>{getPricePerField(adjustment?.netAmount)}</p>
              </li>
            ))}
          </ul>
        )}
        {doesValueExist(totalAdjustments) && !isEBITDAHidden && (
          <div className="bold-border">
            <p>{t('totalAdjustments')}</p>
            <p>{getPricePerField(totalAdjustments)}</p>
          </div>
        )}
        {checkIfNotNegative(adjEBITDA) && !isEBITDAHidden && (
          <div className="bold-border">
            <p>{t('adjustedEBITDA')}</p>
            <p>{getPricePerField(adjEBITDA)}</p>
          </div>
        )}

        <span className="bold-border" />
      </div>
    );
  }, [
    adjEBITDA,
    isLoading,
    listing,
    t,
    isPreview,
    isAccountingPeriodHidden,
    accountingPeriodReason,
    isRevenueHidden,
    revenueReason,
    isCOGSHidden,
    COGSReason,
    isLessOverheadsHidden,
    lessOverheadsReason,
    isDepreciationHidden,
    depreciationReason,
    isEBITDAHidden,
    EBITDAReason,
  ]);

  const bussinesVideo = useMemo(
    () =>
      isLoading ? (
        <VideoPlaceholder />
      ) : (
        <BussinessVideo
          hasVideo={
            listing?.includeVideoInTeaser !== INCLUDE_VIDEO_IN_TEASER.NO
          }
          videoSrc={
            listing?.includeVideoInTeaser ===
            INCLUDE_VIDEO_IN_TEASER.WITH_EMBEDDED_CODE
              ? listing?.embeddedVideoCode
              : listing?.embeddedTeaserVideoFile?.url
          }
        />
      ),
    [
      isLoading,
      listing?.embeddedTeaserVideoFile?.url,
      listing?.embeddedVideoCode,
      listing?.includeVideoInTeaser,
    ],
  );

  // the lead always has to confirm their details (and provide context when the
  // listing requires it) before the enquiry is sent, teaser enabled or not
  const registerInterest = useCallback(
    () => onEnquire(listing?._id),
    [listing?._id, onEnquire],
  );

  const backLink = useMemo(() => {
    const { pathname, search } = previousLocation || {};

    const to =
      search && !search?.includes('/teaser')
        ? `${BUSINESS_ROUTE}${search}`
        : pathname &&
          pathname?.includes(BUSINESS_ROUTE) &&
          !pathname?.includes('/teaser')
        ? pathname
        : BUSINESS_ROUTE;

    return to;
  }, [previousLocation]);

  const handleNotInterested = useCallback(async () => {
    try {
      setIsSubmitting(true);

      if (isLoggedIn) {
        await api.listings.declineInterest(listing?._id);
      }

      navigate(backLink, { replace: true });
    } catch (error) {
      sentryError(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [isLoggedIn, backLink, listing, navigate]);

  //if teaser is not available, redirect
  useEffect(() => {
    if ((needsToBeLoggedIn && !isLoggedIn) || (!enableTeaser && !isPreview)) {
      navigate(BUSINESS_ROUTE);
    }
  }, [
    enableTeaser,
    isLoggedIn,
    needsToBeLoggedIn,
    openEnquiry,
    navigate,
    isPreview,
  ]);

  useEffect(() => {
    if (hasTeaserFile) addFooterClassName?.(DISABLE_FOOTER_CLASS);

    return () => {
      resetFooterClassName?.();
    };
  }, [hasTeaserFile, addFooterClassName, resetFooterClassName]);

  useEffect(() => {
    if (currentUser && listing?._id) {
      api.listings.viewTeaser(listing?._id);
    }
  }, [currentUser, listing?._id]);

  return (
    <div className={classes}>
      {hasTeaserFile ? (
        isImage(listing.teaserFile.type) ? (
          <img
            src={getImgUrl(listing.teaserFile.url, RESIZED_WIDTH[1280])}
            alt={listing.teaserFile.url}
          />
        ) : (
          <div className="business-teaser__pdf-wrapper">
            <PdfViewer url={listing?.teaserFile?.url} canZoom={true} />
          </div>
        )
      ) : (
        <div className="business-teaser__content">
          <div className="business-teaser__content__left">
            <div className="business-teaser__tags">
              <span>
                <LocationmarkerIcon />
                {listing?.location?.state || listing?.dealLocation}
              </span>
              <span>
                <SuitcaseIcon /> {listing?.industry}
              </span>
            </div>

            {isLoading ? (
              <TitlePlaceholder />
            ) : (
              <p className="business-teaser__title">{listing.listingTitle}</p>
            )}

            {isMobile && bussinesVideo}

            {keyDetails}
            {plStatement}
            {askingPrice}
          </div>

          <div className="business-teaser__content__right">
            {!isMobile && bussinesVideo}

            {isLoading ? (
              <MapPlaceholder />
            ) : (
              listing.location && <Map location={listing.location} />
            )}

            {isLoading ? (
              <WhatsNextPlaceholder />
            ) : (
              <WhatsNextSection
                hasNda={!!listing?.envelopeTemplateId}
                isDisabled={isSubmitting}
                {...(!isPreview &&
                  !dontShowEnquire && {
                    onRegisterClick: registerInterest,
                  })}
              />
            )}
          </div>
        </div>
      )}

      {!dontShowEnquire && (
        <NudgeInterested
          areActionsDisabled={isPreview}
          isSubmitting={isSubmitting}
          text={t('businessTeaserNudgeText')}
          notForMe={handleNotInterested}
          registerInterest={registerInterest}
        />
      )}
    </div>
  );
};

export default BusinessTeaser;
